import { useRef, useState } from "react";
import { Button } from "./ui/button";

const CHUNK_DURATION_S = 25;
const SAMPLE_RATE = 16000;
const SAMPLES_PER_CHUNK = CHUNK_DURATION_S * SAMPLE_RATE;
const MAX_FILE_SIZE_MB = 50; // ~15min of MP3 at 128kbps

interface AudioUploadProps {
  onAudioReady: (audioData: Float32Array) => void;
  disabled?: boolean;
}

export function AudioUpload({ onAudioReady, disabled }: AudioUploadProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState("");

  const handleFile = async (file: File) => {
    const sizeMB = file.size / (1024 * 1024);
    if (sizeMB > MAX_FILE_SIZE_MB) {
      setProgress(`File too large (${sizeMB.toFixed(0)}MB). Max ${MAX_FILE_SIZE_MB}MB (~15 min).`);
      setTimeout(() => setProgress(""), 5000);
      return;
    }

    setIsProcessing(true);
    setProgress("Decoding audio...");

    try {
      const arrayBuffer = await file.arrayBuffer();
      const audioCtx = new AudioContext({ sampleRate: SAMPLE_RATE });
      const decoded = await audioCtx.decodeAudioData(arrayBuffer);
      const float32 = decoded.getChannelData(0);
      audioCtx.close();

      const totalChunks = Math.ceil(float32.length / SAMPLES_PER_CHUNK);

      if (totalChunks <= 1) {
        setProgress("Transcribing...");
        onAudioReady(float32);
      } else {
        for (let i = 0; i < totalChunks; i++) {
          const start = i * SAMPLES_PER_CHUNK;
          const end = Math.min(start + SAMPLES_PER_CHUNK, float32.length);
          const chunk = float32.slice(start, end);
          setProgress(`Transcribing chunk ${i + 1} of ${totalChunks}...`);
          onAudioReady(chunk);
          // Small delay to let the worker queue process
          if (i < totalChunks - 1) {
            await new Promise((r) => setTimeout(r, 100));
          }
        }
      }

      setProgress("Upload complete — transcribing...");
    } catch (err) {
      console.error("Audio decode error:", err);
      setProgress(`Error: Could not decode audio file`);
    }

    // Clear processing state after a short delay
    setTimeout(() => {
      setIsProcessing(false);
      setProgress("");
    }, 3000);

    // Reset file input
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
  };

  return (
    <div className="flex items-center gap-3">
      <input
        ref={fileInputRef}
        type="file"
        accept="audio/*"
        onChange={handleChange}
        className="hidden"
      />
      <Button
        variant="outline"
        size="lg"
        onClick={() => fileInputRef.current?.click()}
        disabled={disabled || isProcessing}
        className="h-14 px-6 text-base sm:h-10 sm:px-4 sm:text-sm"
        title="Upload MP3, WAV, M4A, or AAC (max ~15 min)"
      >
        {isProcessing ? "Processing..." : "Upload Audio"}
      </Button>
      {progress ? (
        <span className={`text-xs ${progress.startsWith("File too large") ? "text-destructive" : "text-muted-foreground"}`}>
          {progress}
        </span>
      ) : !isProcessing && (
        <span className="text-xs text-muted-foreground hidden sm:inline">
          Max ~15 min
        </span>
      )}
    </div>
  );
}
